import React, { lazy, Suspense, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Loader } from '@tools/ui/dime';
import { ModuleComponent } from '@tools/ui/types';
import { ComponentNav, COMPONENTS } from './components/ComponentNav';

const AlertShowcase = lazy(() => import('./pages/AlertShowcase'));
const AmountCardShowcase = lazy(() => import('./pages/AmountCardShowcase'));
const BadgeShowcase = lazy(() => import('./pages/BadgeShowcase'));
const BreadcrumbsShowcase = lazy(() => import('./pages/BreadcrumbsShowcase'));
const ButtonShowcase = lazy(() => import('./pages/ButtonShowcase'));
const ButtonDropdownShowcase = lazy(
  () => import('./pages/ButtonDropdownShowcase')
);
const CheckboxShowcase = lazy(() => import('./pages/CheckboxShowcase'));
const DateInputShowcase = lazy(() => import('./pages/DateInputShowcase'));
const DatePickerShowcase = lazy(() => import('./pages/DatePickerShowcase'));
const DividerShowcase = lazy(() => import('./pages/DividerShowcase'));
const DrawerShowcase = lazy(() => import('./pages/DrawerShowcase'));
const FilterGroupShowcase = lazy(() => import('./pages/FilterGroupShowcase'));
const FilterHeaderShowcase = lazy(() => import('./pages/FilterHeaderShowcase'));
const IllustrationsShowcase = lazy(
  () => import('./pages/IllustrationsShowcase')
);
const InsightLabelShowcase = lazy(() => import('./pages/InsightLabelShowcase'));
const LoaderShowcase = lazy(() => import('./pages/LoaderShowcase'));
const LoadingBarShowcase = lazy(() => import('./pages/LoadingBarShowcase'));
const MenuShowcase = lazy(() => import('./pages/MenuShowcase'));
const ModalShowcase = lazy(() => import('./pages/ModalShowcase'));
const NotificationShowcase = lazy(() => import('./pages/NotificationShowcase'));
const NotificationBadgeShowcase = lazy(
  () => import('./pages/NotificationBadgeShowcase')
);
const PaginationShowcase = lazy(() => import('./pages/PaginationShowcase'));
const PaymentCardShowcase = lazy(() => import('./pages/PaymentCardShowcase'));
const PopoverShowcase = lazy(() => import('./pages/PopoverShowcase'));
const PromoCodeInputShowcase = lazy(
  () => import('./pages/PromoCodeInputShowcase')
);
const RadioButtonShowcase = lazy(() => import('./pages/RadioButtonShowcase'));
const RemovableTagShowcase = lazy(() => import('./pages/RemovableTagShowcase'));
const RichTextEditorShowcase = lazy(
  () => import('./pages/RichTextEditorShowcase')
);
const SearchInputShowcase = lazy(() => import('./pages/SearchInputShowcase'));
const SegmentedControlShowcase = lazy(
  () => import('./pages/SegmentedControlShowcase')
);
const SelectShowcase = lazy(() => import('./pages/SelectShowcase'));
const SpiiInputShowcase = lazy(() => import('./pages/SpiiInputShowcase'));
const StatCardShowcase = lazy(() => import('./pages/StatCardShowcase'));
const SwitchShowcase = lazy(() => import('./pages/SwitchShowcase'));
const TabsShowcase = lazy(() => import('./pages/TabsShowcase'));
const TagLabelShowcase = lazy(() => import('./pages/TagLabelShowcase'));
const TextAreaShowcase = lazy(() => import('./pages/TextAreaShowcase'));
const TextInputShowcase = lazy(() => import('./pages/TextInputShowcase'));
const TooltipShowcase = lazy(() => import('./pages/TooltipShowcase'));
const UploadShowcase = lazy(() => import('./pages/UploadShowcase'));

// Paths come from COMPONENTS — single source of truth.
const ROUTE_MAP: Record<string, React.LazyExoticComponent<React.FC>> = {
  [COMPONENTS.find((c) => c.label === 'Alert')!.path]: AlertShowcase,
  [COMPONENTS.find((c) => c.label === 'AmountCard')!.path]: AmountCardShowcase,
  [COMPONENTS.find((c) => c.label === 'Badge')!.path]: BadgeShowcase,
  [COMPONENTS.find((c) => c.label === 'Breadcrumbs')!.path]:
    BreadcrumbsShowcase,
  [COMPONENTS.find((c) => c.label === 'ButtonDropdown')!.path]:
    ButtonDropdownShowcase,
  [COMPONENTS.find((c) => c.label === 'Button')!.path]: ButtonShowcase,
  [COMPONENTS.find((c) => c.label === 'Checkbox')!.path]: CheckboxShowcase,
  [COMPONENTS.find((c) => c.label === 'DateInput')!.path]: DateInputShowcase,
  [COMPONENTS.find((c) => c.label === 'DatePicker')!.path]:
    DatePickerShowcase,
  [COMPONENTS.find((c) => c.label === 'Divider')!.path]: DividerShowcase,
  [COMPONENTS.find((c) => c.label === 'Drawer')!.path]: DrawerShowcase,
  [COMPONENTS.find((c) => c.label === 'FilterGroup')!.path]:
    FilterGroupShowcase,
  [COMPONENTS.find((c) => c.label === 'FilterHeader')!.path]:
    FilterHeaderShowcase,
  [COMPONENTS.find((c) => c.label === 'Illustrations')!.path]:
    IllustrationsShowcase,
  [COMPONENTS.find((c) => c.label === 'InsightLabel')!.path]:
    InsightLabelShowcase,
  [COMPONENTS.find((c) => c.label === 'Loader')!.path]: LoaderShowcase,
  [COMPONENTS.find((c) => c.label === 'LoadingBar')!.path]:
    LoadingBarShowcase,
  [COMPONENTS.find((c) => c.label === 'Menu')!.path]: MenuShowcase,
  [COMPONENTS.find((c) => c.label === 'Modal')!.path]: ModalShowcase,
  [COMPONENTS.find((c) => c.label === 'Notification')!.path]:
    NotificationShowcase,
  [COMPONENTS.find((c) => c.label === 'NotificationBadge')!.path]:
    NotificationBadgeShowcase,
  [COMPONENTS.find((c) => c.label === 'Pagination')!.path]: PaginationShowcase,
  [COMPONENTS.find((c) => c.label === 'PaymentCard')!.path]:
    PaymentCardShowcase,
  [COMPONENTS.find((c) => c.label === 'Popover')!.path]: PopoverShowcase,
  [COMPONENTS.find((c) => c.label === 'PromoCodeInput')!.path]:
    PromoCodeInputShowcase,
  [COMPONENTS.find((c) => c.label === 'RadioButton')!.path]:
    RadioButtonShowcase,
  [COMPONENTS.find((c) => c.label === 'RemovableTag')!.path]:
    RemovableTagShowcase,
  [COMPONENTS.find((c) => c.label === 'RichTextEditor')!.path]:
    RichTextEditorShowcase,
  [COMPONENTS.find((c) => c.label === 'SearchInput')!.path]:
    SearchInputShowcase,
  [COMPONENTS.find((c) => c.label === 'SegmentedControl')!.path]:
    SegmentedControlShowcase,
  [COMPONENTS.find((c) => c.label === 'Select')!.path]: SelectShowcase,
  [COMPONENTS.find((c) => c.label === 'SpiiInput')!.path]: SpiiInputShowcase,
  [COMPONENTS.find((c) => c.label === 'StatCard')!.path]: StatCardShowcase,
  [COMPONENTS.find((c) => c.label === 'Switch')!.path]: SwitchShowcase,
  [COMPONENTS.find((c) => c.label === 'Tabs')!.path]: TabsShowcase,
  [COMPONENTS.find((c) => c.label === 'TagLabel')!.path]: TagLabelShowcase,
  [COMPONENTS.find((c) => c.label === 'TextArea')!.path]: TextAreaShowcase,
  [COMPONENTS.find((c) => c.label === 'TextInput')!.path]: TextInputShowcase,
  [COMPONENTS.find((c) => c.label === 'Tooltip')!.path]: TooltipShowcase,
  [COMPONENTS.find((c) => c.label === 'Upload')!.path]: UploadShowcase
};

// Sorted once at module level — longest first so more specific paths
// (e.g. /button-dropdown) are matched before their prefixes (e.g. /button).
const SORTED_ROUTE_KEYS = Object.keys(ROUTE_MAP).sort(
  (a, b) => b.length - a.length
);

const Container = styled.div`
  display: flex;
  height: 100%;
  overflow: hidden;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
`;

export const DimeShowcaseView: ModuleComponent = () => {
  const { pathname } = useLocation();

  const Page = useMemo(() => {
    const matchedKey = SORTED_ROUTE_KEYS.find((key) =>
      pathname.startsWith(key)
    );
    return ROUTE_MAP[matchedKey ?? ''] ?? AlertShowcase;
  }, [pathname]);

  return (
    <Container>
      <ComponentNav />
      <Content>
        <Suspense
          fallback={
            <LoadingWrapper>
              <Loader />
            </LoadingWrapper>
          }
        >
          <Page />
        </Suspense>
      </Content>
    </Container>
  );
};
